import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const articleLinks = [
  { path: '/guida-itaobuy-spreadsheet-italiano', label: 'Guida Completa' },
  { path: '/itaobuy-e-sicuro', label: 'Itaobuy è Sicuro?' },
  { path: '/come-usare-itaobuy-spreadsheet', label: 'Come Usare' },
  { path: '/itaobuy-spreadsheet-sneakers', label: 'Sneakers' },
  { path: '/itaobuy-spreadsheet-moda', label: 'Moda & Abbigliamento' },
  { path: '/itaobuy-spedizione-italia', label: 'Spedizione Italia' },
  { path: '/itaobuy-prezzi', label: 'Prezzi e Costi' },
  { path: '/itaobuy-vs-alternative', label: 'Itaobuy vs Alternative' },
  { path: '/itaobuy-qc-photos', label: 'QC Photos Guide' },
  { path: '/itaobuy-recensioni', label: 'Recensioni Italiane' },
  { path: '/faq-itaobuy', label: 'FAQ' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (catRef.current && !catRef.current.contains(e.target as Node)) setCatOpen(false);
      if (guideRef.current && !guideRef.current.contains(e.target as Node)) setGuideOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navText = (base: string) =>
    scrolled ? `${base} text-foreground-700 hover:text-foreground-950` : `${base} text-background-50/80 hover:text-background-50`;

  const closeAll = () => { setCatOpen(false); setGuideOpen(false); setMobileOpen(false); };

  const handleNavClick = (path: string) => {
    closeAll();
    navigate(path);
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link
          to="/"
          onClick={closeAll}
          className={`font-heading text-xl font-bold whitespace-nowrap cursor-pointer transition-colors ${
            scrolled ? 'text-foreground-950' : 'text-background-50'
          }`}
        >
          itaobuyspread.baby
        </Link>

        <div className="hidden lg:flex items-center gap-6">
          <Link to="/" onClick={closeAll} className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${navText('')}`}>
            {t('nav.home')}
          </Link>

          <div ref={guideRef} className="relative">
            <button
              onClick={() => { setGuideOpen(!guideOpen); setCatOpen(false); }}
              className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${navText('')}`}
            >
              Guida Acquisti
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guideOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {guideOpen && (
              <div className="absolute top-full left-0 mt-3 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-3 w-[220px] z-50">
                {articleLinks.map((a) => (
                  <button
                    key={a.path}
                    onClick={() => handleNavClick(a.path)}
                    className="block w-full text-left px-5 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 whitespace-nowrap cursor-pointer transition-colors"
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div ref={catRef} className="relative">
            <button
              onClick={() => { setCatOpen(!catOpen); setGuideOpen(false); }}
              className={`flex items-center gap-1 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${navText('')}`}
            >
              {t('nav.categorie')}
              <i className={`ri-arrow-down-s-line transition-transform duration-200 ${catOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {catOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-3 w-[480px] z-50">
                <div className="grid grid-cols-2 gap-1 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 rounded-lg whitespace-nowrap cursor-pointer transition-colors"
                      onClick={closeAll}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className={`hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer border transition-all ${
              scrolled
                ? 'border-primary-500 text-primary-600 hover:bg-primary-50'
                : 'border-background-50/60 text-background-50 hover:bg-background-50/10'
            }`}
          >
            {t('hero.cta_sheet')}
          </a>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-all"
          >
            {t('hero.cta_buy')}
          </a>

          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Menu"
          >
            <i className={`ri-menu-line text-xl ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-foreground-950/50" onClick={closeAll}></div>
          <div className="absolute top-0 right-0 h-full w-[300px] max-w-[85vw] bg-background-50 shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-background-200">
              <span className="font-heading text-lg font-bold text-foreground-950">Menu</span>
              <button onClick={closeAll} className="w-10 h-10 flex items-center justify-center cursor-pointer">
                <i className="ri-close-line text-xl text-foreground-700"></i>
              </button>
            </div>

            <div className="p-4 space-y-6">
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Navigazione</h4>
                <div className="space-y-1">
                  <button onClick={() => handleNavClick('/')} className="block w-full text-left px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer transition-colors">
                    <i className="ri-home-4-line mr-2"></i> {t('nav.home')}
                  </button>
                  <button onClick={() => handleNavClick('/guida-itaobuy-spreadsheet-italiano')} className="block w-full text-left px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer transition-colors">
                    <i className="ri-book-open-line mr-2"></i> Guida Completa
                  </button>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">Guide Acquisti</h4>
                <div className="space-y-1">
                  {articleLinks.slice(1).map((a) => (
                    <button
                      key={a.path}
                      onClick={() => handleNavClick(a.path)}
                      className="block w-full text-left px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg cursor-pointer transition-colors"
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-3">{t('nav.categorie')}</h4>
                <div className="space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block w-full text-left px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg whitespace-nowrap cursor-pointer transition-colors"
                      onClick={closeAll}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-background-200 space-y-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-3 rounded-full text-sm font-medium border border-primary-500 text-primary-600 hover:bg-primary-50 cursor-pointer transition-all"
                  onClick={closeAll}
                >
                  {t('hero.cta_sheet')}
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-3 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer transition-all"
                  onClick={closeAll}
                >
                  {t('hero.cta_buy')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}